/**
 * Timezone utility functions for consistent date/time handling.
 * Default timezone: Asia/Dubai (UTC+4)
 */

export interface DayRange {
  start_local: Date;
  end_local: Date;
  start_utc: Date;
  end_utc: Date;
  date_phrase: string;
}

/**
 * Get date range for a given date phrase in a specific timezone.
 * Returns both local and UTC boundaries.
 * 
 * @param userTz - Timezone string (default: 'Asia/Dubai')
 * @param datePhrase - Date phrase like "today", "yesterday", "this week", or specific date
 * @returns DayRange with local and UTC boundaries
 */
export function getDayRange(
  userTz: string = 'Asia/Dubai',
  datePhrase?: string
): DayRange {
  const now = new Date();
  const phrase = (datePhrase || 'today').toLowerCase().trim();
  
  // Create formatter early for use in "upcoming" case
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  let targetDate: Date;
  
  // Parse date phrase
  if (phrase === 'all') {
    // For "all", return a very wide date range (1 year in past to 1 year in future)
    const startLocal = new Date(now);
    startLocal.setFullYear(startLocal.getFullYear() - 1);
    startLocal.setHours(0, 0, 0, 0);
    
    const endLocal = new Date(now);
    endLocal.setFullYear(endLocal.getFullYear() + 1);
    endLocal.setHours(23, 59, 59, 999);
    
    // Get timezone offset
    const utcOffset = getTimezoneOffset(userTz, now);
    
    // Convert to UTC
    const startUtc = new Date(startLocal.getTime() - utcOffset * 60 * 1000);
    const endUtc = new Date(endLocal.getTime() - utcOffset * 60 * 1000);
    
    return {
      start_local: startLocal,
      end_local: endLocal,
      start_utc: startUtc,
      end_utc: endUtc,
      date_phrase: phrase,
    };
  } else if (phrase === 'upcoming' || phrase === 'future') {
    // For "upcoming" or "future", return range from now to 30 days in the future
    // This is a special case that doesn't use targetDate for start
    const startLocal = new Date(now);
    const endLocal = new Date(now);
    endLocal.setDate(endLocal.getDate() + 30); // 30 days from now
    
    // Get timezone offset
    const utcOffset = getTimezoneOffset(userTz, now);
    
    // Convert to UTC
    const startUtc = new Date(startLocal.getTime() - utcOffset * 60 * 1000);
    
    // For end date, set to end of day (23:59:59.999)
    const endLocalParts = formatter.formatToParts(endLocal);
    const endYear = parseInt(endLocalParts.find(p => p.type === 'year')?.value || '0');
    const endMonth = parseInt(endLocalParts.find(p => p.type === 'month')?.value || '0') - 1;
    const endDay = parseInt(endLocalParts.find(p => p.type === 'day')?.value || '0');
    const endLocalEndOfDay = new Date(endYear, endMonth, endDay, 23, 59, 59, 999);
    const endUtcEndOfDay = new Date(endLocalEndOfDay.getTime() - utcOffset * 60 * 1000);
    
    return {
      start_local: startLocal,
      end_local: endLocalEndOfDay,
      start_utc: startUtc,
      end_utc: endUtcEndOfDay,
      date_phrase: phrase,
    };
  } else if (phrase === 'today' || phrase === '') {
    targetDate = new Date(now);
  } else if (phrase === 'yesterday') {
    targetDate = new Date(now);
    targetDate.setDate(targetDate.getDate() - 1);
  } else if (phrase === 'tomorrow') {
    targetDate = new Date(now);
    targetDate.setDate(targetDate.getDate() + 1);
  } else if (phrase.startsWith('this week')) {
    // Start of current week (Sunday)
    targetDate = new Date(now);
    const day = targetDate.getDay();
    targetDate.setDate(targetDate.getDate() - day);
  } else if (phrase.startsWith('last week')) {
    // Start of last week
    targetDate = new Date(now);
    const day = targetDate.getDay();
    targetDate.setDate(targetDate.getDate() - day - 7);
  } else if (phrase.startsWith('next week')) {
    // Start of next week
    targetDate = new Date(now);
    const day = targetDate.getDay();
    targetDate.setDate(targetDate.getDate() - day + 7);
  } else if (phrase === 'this month' || phrase === 'current month') {
    // Return range for entire current month
    const startLocal = new Date(now);
    startLocal.setDate(1); // First day of month
    startLocal.setHours(0, 0, 0, 0);
    
    const endLocal = new Date(now);
    endLocal.setMonth(endLocal.getMonth() + 1); // Next month
    endLocal.setDate(0); // Last day of current month
    endLocal.setHours(23, 59, 59, 999);
    
    const utcOffset = getTimezoneOffset(userTz, now);
    const startUtc = new Date(startLocal.getTime() - utcOffset * 60 * 1000);
    const endUtc = new Date(endLocal.getTime() - utcOffset * 60 * 1000);
    
    return {
      start_local: startLocal,
      end_local: endLocal,
      start_utc: startUtc,
      end_utc: endUtc,
      date_phrase: phrase,
    };
  } else if (phrase === 'last month' || phrase === 'previous month') {
    // Return range for entire previous month
    const startLocal = new Date(now);
    startLocal.setMonth(startLocal.getMonth() - 1); // Previous month
    startLocal.setDate(1); // First day of that month
    startLocal.setHours(0, 0, 0, 0);
    
    const endLocal = new Date(now);
    endLocal.setDate(0); // Last day of previous month
    endLocal.setHours(23, 59, 59, 999);
    
    const utcOffset = getTimezoneOffset(userTz, now);
    const startUtc = new Date(startLocal.getTime() - utcOffset * 60 * 1000);
    const endUtc = new Date(endLocal.getTime() - utcOffset * 60 * 1000);
    
    return {
      start_local: startLocal,
      end_local: endLocal,
      start_utc: startUtc,
      end_utc: endUtc,
      date_phrase: phrase,
    };
  } else {
    // Try to parse as specific date (YYYY-MM-DD or similar formats)
    const parsed = new Date(phrase);
    if (!isNaN(parsed.getTime())) {
      targetDate = parsed;
    } else {
      // Fallback to today if parsing fails
      targetDate = new Date(now);
    }
  }
  
  // Get timezone offset in minutes for the target date
  const utcOffset = getTimezoneOffset(userTz, targetDate);
  
  // For proper timezone conversion:
  // 1. Get the date components in the target timezone
  // 2. Create start/end of day in that timezone
  // 3. Convert to UTC by subtracting the offset
  
  // Get year, month, day in the target timezone
  // Use Intl.DateTimeFormat to get accurate date components in the target timezone
  const parts = formatter.formatToParts(targetDate);
  const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
  const month = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1; // 0-indexed
  const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
  
  // CRITICAL FIX: Create dates in the target timezone properly
  // We need to create a date string in the target timezone and parse it
  // Format: "YYYY-MM-DD HH:mm:ss" in target timezone, then convert to UTC
  const startLocalStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`;
  const endLocalStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T23:59:59.999`;
  
  // Use Intl.DateTimeFormat to properly convert from target timezone to UTC
  // Create a date in UTC that represents midnight in the target timezone
  // For UTC+4 (Dubai): midnight Dubai = 20:00 previous day UTC
  // So we create the date as if it's in UTC, then adjust
  
  // Better approach: Create date in UTC that represents the local time in target timezone
  // For start: We want YYYY-MM-DD 00:00:00 in target timezone
  // If target is UTC+4, then 00:00:00 Dubai = 20:00:00 previous day UTC
  // So: create date for YYYY-MM-DD 00:00:00 UTC, then subtract offset to get the UTC time
  // Actually: if we want 00:00:00 in UTC+4, that's 20:00:00 previous day in UTC
  // So: create (YYYY-MM-DD 00:00:00 UTC) - 4 hours = start_utc
  
  // Correct calculation: 
  // - Create a date object representing YYYY-MM-DD 00:00:00 in UTC
  // - This represents a moment in time
  // - To get what time that moment is in Dubai (UTC+4), we add 4 hours
  // - So if we want 00:00:00 Dubai time, we need 20:00:00 previous day UTC
  // - Therefore: start_utc = (YYYY-MM-DD 00:00:00 UTC) - 4 hours
  
  // Create date for midnight UTC on the target date
  const startUtcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  // Subtract offset to get the UTC time that corresponds to midnight in target timezone
  const startUtc = new Date(startUtcDate.getTime() - utcOffset * 60 * 1000);
  
  // For end of day: 23:59:59.999 in target timezone
  const endUtcDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  const endUtc = new Date(endUtcDate.getTime() - utcOffset * 60 * 1000);
  
  // Create local date objects for display (these represent the local time, not UTC)
  const startLocal = new Date(year, month, day, 0, 0, 0, 0);
  const endLocal = new Date(year, month, day, 23, 59, 59, 999);
  
  return {
    start_local: startLocal,
    end_local: endLocal,
    start_utc: startUtc,
    end_utc: endUtc,
    date_phrase: phrase,
  };
}

/**
 * Get timezone offset in minutes for a given timezone and date.
 * Simplified implementation - for production, use a proper timezone library.
 */
function getTimezoneOffset(tz: string, date: Date): number {
  // Common timezone offsets (in minutes from UTC)
  const offsets: Record<string, number> = {
    'Asia/Dubai': 240, // UTC+4
    'UTC': 0,
    'America/New_York': -300, // UTC-5 (EST) or -240 (EDT) - simplified
    'Europe/London': 0, // UTC+0 (GMT) or 60 (BST) - simplified
  };
  
  // For Asia/Dubai, always UTC+4 (no DST)
  if (tz === 'Asia/Dubai') {
    return 240;
  }
  
  // For other timezones, use a simplified offset
  // In production, use Intl.DateTimeFormat or a library like date-fns-tz
  return offsets[tz] || 0;
}

/**
 * Format a date in the target timezone for display.
 * Uses Intl.DateTimeFormat with timeZone option - no manual offset needed.
 */
export function formatDateInTimezone(
  date: Date,
  userTz: string = 'Asia/Dubai',
  format: 'date' | 'datetime' | 'time' = 'datetime'
): string {
  // toLocaleString handles timezone conversion automatically via timeZone option
  // Do NOT add manual offset - that causes double-conversion
  if (format === 'date') {
    return date.toLocaleDateString('en-US', {
      timeZone: userTz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } else if (format === 'time') {
    return date.toLocaleTimeString('en-US', {
      timeZone: userTz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } else {
    return date.toLocaleString('en-US', {
      timeZone: userTz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
}

